import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import { DEFAULT_ERROR_TEXT } from '@utils';
import {
  ISalesForceBusinessUserDrawerInfo,
  ISalesForceBusinessUserDrawerProps,
} from '.';
import {
  getBusinessUserLovs,
  checkBusinessUserByPerson,
  createBusinessUser,
  updateBusinessUser,
  getPersonEmailDetailsInfo,
} from './queries';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useNavigate } from 'react-router-dom';
import { getActivePersonsQuery } from './queries';
import { extractPersons, toLookups } from './utils';
import { isEmpty } from '@utils/validationUtils';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { IPagination } from '@models';

const SalesForceBusinessUserDrawer: React.FC<
  ISalesForceBusinessUserDrawerProps
> = ({
  open,
  onSuccess,
  onClose,
  salesForceBusinessUserDetailsInfo,
  businessUserId,
}) => {
  const navigate = useNavigate();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [defaultValues, setDefaultValues] =
    useState<ISalesForceBusinessUserDrawerInfo>();

  const [lookups, setLookups] = useState<
    Record<string, Record<string, string>>
  >({});

  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    drawerSections(businessUserId, defaultValues, lookups)
  );

  const [booted, setBooted] = useState<boolean>(false);

  const [getActivePersonsLazy, { loading }] = useLazyQuery(
    getActivePersonsQuery
  );
  const [getBusinessUserLovsLazy] = useLazyQuery(getBusinessUserLovs);
  const [getPersonEmailDetailsLazy] = useLazyQuery(getPersonEmailDetailsInfo);
  const [checkBusinessUserByPersonLazy] = useLazyQuery(
    checkBusinessUserByPerson
  );
  const [salesForceBusinessUserAction] = useMutation(
    businessUserId ? updateBusinessUser : createBusinessUser
  );

  const initialize = async () => {
    try {
      const result = await getBusinessUserLovsLazy();
      if (result.data) {
        const lookupLists = toLookups(result.data);
        setLookups(lookupLists);

        setInputsForm(
          drawerSections(
            businessUserId,
            salesForceBusinessUserDetailsInfo,
            lookupLists
          )
        );
      }

      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const validateBusinessUserExistsOnPersonChange = async (
    selectedPersonID: string
  ) => {
    try {
      if (!isEmpty(selectedPersonID)) {
        const result = await checkBusinessUserByPersonLazy({
          variables: {
            selectedPersonID: selectedPersonID,
          },
        });

        if (
          result.data.SalesforceManagement?.queries?.checkBusinessUserByPerson
            .length > 0
        ) {
          return `Business User already created for 
          ${result.data.SalesforceManagement?.queries?.checkBusinessUserByPerson[0]?.salesforceManagement_BusinessUser_PersonID?.views?.SalesforceManagement_all?.properties?.PersonCode}
           - 
          ${result.data.SalesforceManagement?.queries?.checkBusinessUserByPerson[0]?.salesforceManagement_BusinessUser_PersonID?.views?.SalesforceManagement_all?.properties?.FullName}`;
        }
      }
    } catch (err) {
      //
    }

    return '';
  };

  const getPersonEmailDetails = async (
    selectedPersonID: string
  ): Promise<string> => {
    try {
      if (!isEmpty(selectedPersonID)) {
        const result = await getPersonEmailDetailsLazy({
          variables: {
            id: selectedPersonID,
          },
        });
        return (
          result.data?.SalesforceManagement?.entities?.person?.views
            ?.SalesforceManagement_all?.properties?.Email || ''
        );
      }
    } catch (err) {
      //
    }
    return '';
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(
      drawerSections(businessUserId, defaultValues, lookups),
      values
    );

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      let variablesMutation = {};
      if (!businessUserId) {
        variablesMutation = {
          businessUserInputs: {
            personID: data.person,
            password: data.password || null,
            type: data.type,
            account: data.account || null,
            access: data.access,
            status: data.SalesforceManagement_BusinessUserStatuses,
            isAdmin: data.isAdmin,
          },
        };
      } else {
        variablesMutation = {
          personID: salesForceBusinessUserDetailsInfo.personId,
          type: data.type,
          account: data.account || null,
          access: data.access,
          isAdmin: data.isAdmin,
        };
      }

      const res = await salesForceBusinessUserAction({
        variables: businessUserId
          ? {
              ...variablesMutation,
              entityId: businessUserId,
            }
          : variablesMutation,
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {businessUserId
              ? 'Business User successfully updated'
              : 'Business User successfully created'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          onSuccess();
          onClose();
          if (!businessUserId) {
            const newbusinessUserId =
              res.data.salesforceManagement.actions.createBusinessUser.id;
            navigate(`/salesforce/business-users/` + newbusinessUserId);
          }
          setFormDisabled(false);
        }, 500);
      } else {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (err) {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
    }
  };

  const onCustomChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    allValues: Record<string, any>,
    errors: Record<string, any>,
    touched: Record<string, boolean>
  ) => {
    allValues[fieldName] = value;
    if (fieldName === 'person') {
      const [newEmailAddress, businessUserValidation] = await Promise.all([
        getPersonEmailDetails(value),
        validateBusinessUserExistsOnPersonChange(value),
      ]);

      allValues.email = newEmailAddress || '';

      errors.person = businessUserValidation || '';
      touched.person = true;
    }

    if (fieldName == 'access') {
      allValues.isAdmin = false;
    }

    setDefaultValues(allValues as any);
  };

  const handleSearch = async (
    fieldName: string,
    value: string,
    pagination: IPagination
  ) => {
    if (fieldName === 'person') {
      return getActivePersonsLazy({
        variables: {
          pagination,
          keywordSearch: value,
        },
      }).then((result) => {
        const extractedPersons = extractPersons(result.data);
        return {
          options: extractedPersons,
          totalCount:
            result.data.SalesforceManagement.queries.getPersonList.paging
              .totalCount,
        };
      });
    }

    return new Promise<{
      options: Record<string, string>;
      totalCount: number;
    }>((resolve) => {
      resolve({ options: {}, totalCount: 0 });
    });
  };

  return (
    <DynamicFormDrawer
      title={businessUserId ? 'Modify Business User' : 'New Business User'}
      loading={!booted}
      onClose={() => onClose()}
      isOpen={open}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      loadingFields={{
        person: loading,
      }}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onChange={onCustomChange}
      onAutocompleteSearch={handleSearch}
    />
  );
};

export default SalesForceBusinessUserDrawer;
